import { useState } from "react";
import { useMsal, useMsalAuthentication } from "@azure/msal-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Modal from "../Modal/Modal";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { protectedResources } from "../../authConfig";
import { IQuizQuestion } from "../../model/QuizQuestion.model";
import { IQuizQuestionAnswer } from "../../model/QuizQuestionAnswer.model";
import { set, useForm } from "react-hook-form";
import QuizResultTextComponent, { LEADERSHIPNAME, PMNAME, SMENAME } from "./QuizResultTextComponent";
import './QuizComponent.scss';

const QuizComponent: React.FC = () => {
  const { register, handleSubmit, reset } = useForm();
  const { instance } = useMsal();
  const [account, setAccount] = useState(instance.getActiveAccount());
  const { error, execute } = useFetchWithMsal({
    scopes: protectedResources.apiCFSkillPath.scopes.read,
  });
  const [loading, setLoading] = useState(false);
  const [callErr, setErr] = useState(null);
  const [questions, setQuestions] = useState<IQuizQuestion[]>([]);
  const [questionIndex, setQuestionIndex] = useState(1);
  const [currentQuestion, setCurrentQuestion] = useState<
    IQuizQuestion | undefined
  >(undefined);
  const [currentAnswer, setCurrentAnswer] = useState<
    IQuizQuestionAnswer | undefined
  >(undefined);
  const [finished, setFinished] = useState(false);
  const [totalScore, setTotalScore] = useState(0);
  const [lScore, setLScore] = useState(0);
  const [pmScore, setPMScore] = useState(0);
  const [smeScore, setSMEScore] = useState(0);
  const [resultPieData, setResultData] = useState([
    { name: PMNAME, value: 300 },
    { name: LEADERSHIPNAME, value: 400 },
    { name: SMENAME, value: 300 },
  ]);
  const pieColors = ["#574887", "#009b72", "#fed103"];
  const getQuizData = () => {
    let acc = account;
    setLoading(true);
    execute("GET", `/api/QuizQuestionsAnswers`)
      .then((response) => {
        if (response.error) {
          return;
        }
        //setSLData(response);
        setQuestions(response);
        setLoading(false);
        if (response.length > 0) {
          setCurrentQuestion(response[0]);
        }
        //setOdataStringSt(odataString);
      })
      .catch((err) => {
        setErr(err);
        setLoading(false);
      });
  };
  if (!loading && questions.length < 1) {
    getQuizData();
  }
  const onCurrentAnswerChange = (event: any) => {
    if (event.target.value) {
      (currentQuestion as any).usersAnswer = currentQuestion?.answers?.find(
        (a) => a.quizQuestionAnswerId === event.target.value
      );
      setCurrentAnswer(
        currentQuestion?.answers?.find(
          (a) => a.quizQuestionAnswerId === event.target.value
        )
      );
    }
  };
  
  const goToNextQuestion = () => {
    setCurrentAnswer(undefined);
    var currentAnswerId = currentAnswer?.quizQuestionAnswerId;
    if (currentAnswerId) reset({ quizanswers: "" });
    setCurrentQuestion(questions[questionIndex]);
    setQuestionIndex(questionIndex + 1);
  };
  const finishQuiz = () => {
    var totalScore = questions.reduce((a: number, b: any) => {
      return b.usersAnswer.genericweight + a;
    }, 0);
    var lScore = questions.reduce((a: number, b: any) => {
      return b.usersAnswer.lweight + a;
    }, 0);
    var pmScore = questions.reduce((a: number, b: any) => {
      return b.usersAnswer.pmweight + a;
    }, 0);
    var smeScore = questions.reduce((a: number, b: any) => {
      return b.usersAnswer.smeweight + a;
    }, 0);
    if (resultPieData) {
      let leadr = resultPieData.find((x) => x.name === LEADERSHIPNAME);
      if (leadr) leadr.value = lScore;
      let pm = resultPieData.find((x) => x.name === PMNAME);
      if (pm) pm.value = pmScore;
      let sme = resultPieData.find((x) => x.name === SMENAME);
      if (sme) sme.value = smeScore;
      setResultData(resultPieData);
    }
    var sorted = resultPieData.sort((a, b) => {
      if (a.value < b.value) return 1;
      if (a.value === b.value) return 0;
      return -1;
    });

    console.log(sorted);
    setTotalScore(totalScore);
    setLScore(lScore);
    setPMScore(pmScore);
    setSMEScore(smeScore);

    setFinished(true);
  };
  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
  }: any) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <>
      {finished && (
        <>
          <div className="fakeQuizplaceholder">
            <h3 className="fnt-wght-bold clr-secondary">Completed Quiz</h3>
            <fieldset className="m-t-1 no-border">
              <QuizResultTextComponent resultList={resultPieData} />
              <div className='m-auto'>
                <div className="quiz-component-responsive-container">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={resultPieData}
                        isAnimationActive={false}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {resultPieData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={pieColors[index % pieColors.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <h4 className="aln-left m-t-2" id="scoringTotals">Scoring Totals</h4>
              <ul className="reset" aria-labelledby="scoringTotals">
                <li className="disp-block aln-left m-b--50">
                  {LEADERSHIPNAME} Score: <b>{lScore}</b>
                </li>
                <li className="disp-block aln-left m-b--50">
                  {PMNAME} Score: <b>{pmScore}</b>
                </li>
                <li className="disp-block aln-left">
                  {SMENAME} Score: <b>{smeScore}</b>
                </li>
              </ul>
            </fieldset>
          </div>
        </>
      )}

      {!finished &&
        questions &&
        questions.length > 0 &&
        currentQuestion !== undefined && (
          <>
            <div className="fakeQuizplaceholder">
              <h3 className="fnt-wght-bold clr-secondary">
                Question {questionIndex} of {questions.length}
              </h3>
              <fieldset className="m-t-1 no-border">
                <legend>{currentQuestion.question}</legend>
                <div

                  onChange={onCurrentAnswerChange}
                >
                  {currentQuestion.answers !== undefined &&
                    currentQuestion.answers.length > 0 &&
                    currentQuestion.answers.map((a, i) => (
                      <div
                        key={i}
                        className="quiz-component-radio-button-container"
                      >
                        <input
                          id={a.quizQuestionAnswerId}
                          type="radio"
                          value={a.quizQuestionAnswerId}
                          {...register("quizanswers")}
                        />
                        <label htmlFor={a.quizQuestionAnswerId} className="aln-left">
                          {a.answer}
                        </label>
                      </div>
                    ))}
                </div>
              </fieldset>
              <div className="dis-inline-flex dis-flex-rows dis-flex-aln-center gap--75 m-t-1">
                {questionIndex < questions.length &&
                  currentAnswer !== undefined && (
                    <button
                      className="btn-secondary"
                      onClick={goToNextQuestion}
                    >
                      Next
                    </button>
                  )}
                {questionIndex === questions.length &&
                  currentAnswer !== undefined && (
                    <button className="btn-secondary" onClick={finishQuiz}>
                      Finish
                    </button>
                  )}
              </div>
            </div>
          </>
        )}
    </>
  );
};

export default QuizComponent;
